import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../../apiConfig';

const vinculacionApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Accept': 'application/json',
    }
});

/**
 * Interceptor de respuestas:
 * - Si viene error.response, dejamos que cada función lo maneje.
 * - Si no hay response (network/CORS), lanzamos un Error claro.
 */
vinculacionApi.interceptors.response.use(
    response => response,
    error => {
        if (!error.response) {
            // No llegó respuesta del servidor: CORS o red caída
            const msg = 'Error de red o CORS: no se pudo conectar al servidor';
            toast.error(msg, { position: 'top-right' });
            return Promise.reject(new Error(msg));
        }

        const { status, data } = error.response;

        // Si vienen errores de validación en data.errors
        if (data.errors && typeof data.errors === 'object') {
            // aplanar arrays y mostrarlos todos
            Object.values(data.errors)
                .flat()
                .forEach(msg => toast.error(msg));
        }
        // si viene message genérico
        else {
            const msg =
                data.message ||
                `Error en servidor (${status})`;
            toast.error(msg);
        }

        return Promise.reject(error);
    }
);


// -----------------------------------------------------------------------------
// GET endpoints
// -----------------------------------------------------------------------------
export async function fetchGeneros() {
    try {
        const resp = await vinculacionApi.get('/generos');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchTiposDocumento() {
    try {
        const resp = await vinculacionApi.get('/tiposDocumento');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchNaturalezas() {
    try {
        const resp = await vinculacionApi.get('/naturalezas');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchPaises() {
    try {
        const resp = await vinculacionApi.get('/paises');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchMunicipios() {
    try {
        const resp = await vinculacionApi.get('/municipios');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchLicencias() {
    try {
        const resp = await vinculacionApi.get('/licencias');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchTiposVehiculos() {
    try {
        const resp = await vinculacionApi.get('/tiposVehiculo');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchEmpresasSeguros() {
    try {
        const resp = await vinculacionApi.get('/empresasSeguros');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
}

export async function fetchMarcas() {
    try {
        const resp = await vinculacionApi.get('/marcas');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
}

export async function fetchLineas({ codigoMarca }) {
    try {
        const resp = await vinculacionApi.get('/lineas', {
            params: { codigoMarca }
        });
        return resp.data.data;
    } catch (error) {
        throw error;
    };
}

export async function fetchCombustibles() {
    try {
        const resp = await vinculacionApi.get('/combustibles');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
}

export async function fetchCarrocerias() {
    try {
        const resp = await vinculacionApi.get('/carrocerias');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchConfiguraciones() {
    try {
        const resp = await vinculacionApi.get('/configuraciones');
        return resp.data.data;
    } catch (error) {
        throw error;
    };
};

export async function fetchEmpresaByTenant(tenant) {
    try {
        const resp = await vinculacionApi.get(`/empresaCodigo/${tenant}`);
        return resp.data.empresa;
    } catch (error) {
        if (!error.response) {
            throw error;
        }
        const status = error.response.status;
        throw new Error(
            status === 404
                ? 'Empresa no encontrada'
                : status === 400
                    ? 'Tenant inválido'
                    : `Error en servidor (${status})`
        );
    }
}

export function getLogoUrls(nit) {
    const baseUrl = 'https://api.app.safiteapp.com/assets/images/Empresas';
    const extensions = ['png', 'jpg'];
    const prefixes = ['Logo_', 'logo_', 'Logo', 'logo'];

    const urls = [];
    
    // Generar todas las combinaciones posibles
    for (const prefix of prefixes) {
        for (const ext of extensions) {
            urls.push(`${baseUrl}/${prefix}${nit}.${ext}`);
        }
    }

    return urls;
}

// -----------------------------------------------------------------------------
// POST - Envío de datos
// -----------------------------------------------------------------------------
export async function createVinculacion(data) {
    try {
        const resp = await vinculacionApi.post('/vinculacion', data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return resp.message;
    } catch (error) {
        throw error;
    };
}

export default vinculacionApi;
